/**
 * TeamSystem Impact - Dashboard
 * Vista contributore: attività personali.
 * Vista amministratore/responsabile ESG: il "respiro" della compliance
 * (percorso Inspira → Espira con drill-down), gestita da CompliancePathDashboard.
 */

import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  DataUsage as DataUsageIcon,
  Notifications as NotificationsIcon,
  Balance as BalanceIcon,
  FactCheck as FactCheckIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { useWorkingYear } from '../../store/workingYearStore';
import { useUserAssignments } from '../data-capture/hooks/useUserAssignments';
import { usePendingConsolidations } from '../data-capture/hooks/usePendingConsolidations';
import { useUserDmaAssignments } from '../dma/hooks/useUserDmaAssignments';
import { CompliancePathDashboard } from './compliance-path/CompliancePathDashboard';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  progress?: number;
  statusChip?: { label: string; color: 'success' | 'warning' | 'default' };
}

const StatCard = ({ title, value, subtitle, icon, color, progress, statusChip }: StatCardProps) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {statusChip && (
              <Box sx={{ mt: 0.5 }}>
                <Chip label={statusChip.label} color={statusChip.color} size="small" />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
        {progress !== undefined && (
          <Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.completamento')}
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: `${color}20`,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: color,
                },
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { utente } = useAuthStore();
  const { year } = useWorkingYear();

  const isContributore = utente?.ruolo === 'contributore';

  const { hasOwnerAssignments, hasSupervisorAssignments, ownerToDoCount, supervisorToDoCount } = useUserAssignments();
  const pendingConsolidations = usePendingConsolidations();
  const { hasDmaAssessments } = useUserDmaAssignments();
  const { unreadCount } = useNotificationStore();

  // Dashboard semplificata per il ruolo contributore: solo le sue attività personali
  if (isContributore) {
    const noAssignments = !hasOwnerAssignments && !hasSupervisorAssignments && !hasDmaAssessments;
    const clickableProps = (target: string, ariaLabel: string) => ({
      onClick: () => navigate(target),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(target);
        }
      },
      role: 'button',
      tabIndex: 0,
      'aria-label': ariaLabel,
      sx: {
        cursor: 'pointer',
        borderRadius: 1,
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      },
    });
    return (
      <Box>
        <Box mb={4}>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            {t('dashboard.welcome', { name: utente?.nome })}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('dashboard.contributorSubtitle', { year })}
          </Typography>
        </Box>

        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Box {...clickableProps('/inbox', t('dashboard.openInbox'))}>
              <StatCard
                title={t('dashboard.unreadTitle')}
                value={unreadCount}
                subtitle={unreadCount > 0 ? t('dashboard.openInbox') : t('dashboard.allRead')}
                icon={<NotificationsIcon fontSize="large" />}
                color="#9C27B0"
              />
            </Box>
          </Grid>
          {hasOwnerAssignments && (
            <Grid item xs={12} sm={6} md={3}>
              <Box {...clickableProps('/data/collection?tab=my-data', t('dashboard.goToCollection'))}>

                <StatCard
                  title={t('dashboard.datapointsToFill')}
                  value={ownerToDoCount}
                  subtitle={t('dashboard.goToCollection')}
                  icon={<DataUsageIcon fontSize="large" />}
                  color="#2E7D32"
                />
              </Box>
            </Grid>
          )}
          {hasSupervisorAssignments && (
            <Grid item xs={12} sm={6} md={3}>
              <Box {...clickableProps('/data/validation', t('dashboard.goToValidation'))}>
                <StatCard
                  title={t('dashboard.datapointsToValidate')}
                  value={supervisorToDoCount}
                  subtitle={t('dashboard.goToValidation')}
                  icon={<FactCheckIcon fontSize="large" />}
                  color="#ED6C02"
                />
              </Box>
            </Grid>
          )}
          {hasSupervisorAssignments && pendingConsolidations > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <Box {...clickableProps('/data/validation?tab=consolidation', t('dashboard.goToConsolidation'))}>
                <StatCard
                  title={t('dashboard.datapointsToConsolidate')}
                  value={pendingConsolidations}
                  subtitle={t('dashboard.goToConsolidation')}
                  icon={<LayersIcon fontSize="large" />}
                  color="#00838F"
                />
              </Box>
            </Grid>
          )}
          {hasDmaAssessments && (
            <Grid item xs={12} sm={6} md={3}>
              <Box {...clickableProps('/dma/assessment', t('dashboard.dmaAssessments'))}>
                <StatCard
                  title={t('dashboard.dmaAssessments')}
                  value="→"
                  subtitle={t('dashboard.openYourAssessments')}
                  icon={<BalanceIcon fontSize="large" />}
                  color="#0066CC"
                />
              </Box>
            </Grid>
          )}
        </Grid>

        {noAssignments && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary">
                {t('dashboard.noAssignments', { year })}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  // Vista amministratore / responsabile ESG: il "respiro" della compliance
  return <CompliancePathDashboard year={year} />;
};
